/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useWBL } from '../../context/WBLContext';
import { 
  Users, 
  MapPin, 
  Calendar, 
  ChevronRight,
  ClipboardCheck,
  Clock,
  Briefcase,
  User,
  CheckCircle2,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Search,
  Check,
  Filter,
  LayoutList,
  LayoutGrid,
  ArrowUpDown,
  RotateCcw,
  FileText,
  Mail,
  School,
  ExternalLink
} from 'lucide-react';
import { StudentAvatar, isUrl } from '../Common/StudentAvatar';
import { ProfessionalOrgFallback } from '../Common/ProfessionalOrgFallback';
import { HeroBanner } from '../Common/HeroBanner';
import { SearchBar } from '../Common/SearchBar';
import { PlacementDetail } from './PlacementDetail';
import { formatLastNameFirst, sortStudentsByLastName } from '../../utils/studentUtils';
import { doesRecordBelongToOrg } from '../../utils/orgTeamUtils';
import { motion, AnimatePresence } from 'motion/react';

export const PlacementsPage: React.FC = () => {
  const {
    activeUser,
    placements,
    setCurrentView,
    activeTenant,
    users,
    schools,
    viewingStudentId,
    selectedTerm,
    attendance,
    opportunities,
    recommendations,
    currentView,
    applications,
    interviews,
  } = useWBL();

  const userRole = activeUser?.role || 'student';
  const orgName = activeUser?.orgName || '';
  const isStudentView = userRole === 'student' || userRole === 'parent';

  // Toolbar & filter states
  const [collapsedOpps, setCollapsedOpps] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'hired' | 'board' | 'completed'>('all');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [cardStatusDropdownOpen, setCardStatusDropdownOpen] = useState(false);
  const [studentStatusDropdownOpen, setStudentStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const cardStatusDropdownRef = useRef<HTMLDivElement>(null);
  const studentStatusDropdownRef = useRef<HTMLDivElement>(null);
  const [filterOpportunityId, setFilterOpportunityId] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table');
  const [sortBy, setSortBy] = useState<
    'name_asc' | 'name_desc' | 'school' | 'status' | 'status_desc' | 'opp_asc' | 'opp_desc' | 'program_asc' | 'program_desc'
  >('name_asc');

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
      if (cardStatusDropdownRef.current && !cardStatusDropdownRef.current.contains(event.target as Node)) {
        setCardStatusDropdownOpen(false);
      }
      if (studentStatusDropdownRef.current && !studentStatusDropdownRef.current.contains(event.target as Node)) {
        setStudentStatusDropdownOpen(false);
      }
    }
    if (statusDropdownOpen || cardStatusDropdownOpen || studentStatusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [statusDropdownOpen, cardStatusDropdownOpen, studentStatusDropdownOpen]);

  const isDetailView = currentView === 'placement-detail';

  useEffect(() => {
    if (isDetailView) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isDetailView]);

  // Base filtered placements by role, tenant, and term
  const baseFilteredPlacements = useMemo(() => {
    return placements.filter((p) => {
      const opp = opportunities.find((o) => o.id === p.opportunityId);

      // 0. Term filter
      const placementTerm = p.term || opp?.term || opp?.semester;
      if (selectedTerm && placementTerm) {
        if (placementTerm.toLowerCase().trim() !== selectedTerm.toLowerCase().trim()) {
          return false;
        }
      }

      // 1. Region / Tenant filter
      if (activeTenant?.id && activeTenant.id !== 'global') {
        const pTenantId =
          p.tenantId ||
          opp?.tenantId ||
          (users || []).find((u) => u.uid === p.studentId || u.email === p.studentEmail)?.tenantId;
        if (pTenantId && pTenantId !== activeTenant.id) {
          return false;
        }
      }

      // 2. Role-specific filters
      if (userRole === 'organization') {
        return (
          p.organizationName.toLowerCase().trim() === orgName.toLowerCase().trim() ||
          doesRecordBelongToOrg(p, activeUser, users) ||
          (opp ? doesRecordBelongToOrg(opp, activeUser, users) : false)
        );
      }
      if (userRole === 'superadmin' || userRole === 'regional_admin') {
        return true;
      }
      if (userRole === 'advisor') {
        if (!activeUser?.verified) {
          return false;
        }
        const referenceStrings = Array.from(
          new Set(
            [
              activeUser?.schoolId,
              ...(activeUser?.schoolsServedIds || []),
              activeUser?.school,
              ...(activeUser?.schoolsServed || []),
            ].filter(Boolean) as string[]
          )
        );

        const advisorSchools = schools.filter((s) => {
          return referenceStrings.some((ref) => {
            const normRef = ref.toLowerCase().trim();
            const normSchoolName = s.name.toLowerCase().trim();
            const normSchoolId = s.id.toLowerCase().trim();
            return normRef === normSchoolId || normRef === normSchoolName;
          });
        });
        const advisorSchoolIds = advisorSchools.map((s) => s.id);
        const advisorSchoolNames = advisorSchools.map((s) => s.name.toLowerCase().trim());

        const advisorStudents = (users || []).filter((u) => {
          if (u.role !== 'student') return false;
          const ferpaGranted =
            typeof u.ferpaConsent === 'object'
              ? u.ferpaConsent?.granted === true
              : u.ferpaConsent === true;
          if (!ferpaGranted) return false;

          if (u.schoolId && advisorSchoolIds.includes(u.schoolId)) return true;
          if (u.school && advisorSchoolNames.includes(u.school.toLowerCase().trim())) return true;
          return false;
        });
        const studentIds = advisorStudents.map((u) => u.uid);

        return studentIds.includes(p.studentId);
      }
      return p.studentId === (viewingStudentId || (userRole === 'parent' ? activeUser?.childId : activeUser?.uid));
    });
  }, [placements, opportunities, selectedTerm, activeTenant.id, userRole, orgName, activeUser, schools, users, viewingStudentId]);

  // Unique opportunities for dropdown filtering
  const availableOpportunities = useMemo(() => {
    const oppMap = new Map<string, { id: string; title: string; count: number }>();
    baseFilteredPlacements.forEach((p) => {
      const id = p.opportunityId || 'unassigned';
      const opp = opportunities.find((o) => o.id === id);
      const title = opp?.title || p.opportunityTitle || 'WBL Placement';
      if (!oppMap.has(id)) {
        oppMap.set(id, { id, title, count: 1 });
      } else {
        oppMap.get(id)!.count++;
      }
    });
    return Array.from(oppMap.values());
  }, [baseFilteredPlacements, opportunities]);

  // Placement stage counts
  const placementCounts = useMemo(() => {
    const total = baseFilteredPlacements.length;
    const active = baseFilteredPlacements.filter((p) => p.status === 'active').length;
    const hired = baseFilteredPlacements.filter((p) => p.status === 'hired' || !!p.isHiredByOrg).length;
    const board = baseFilteredPlacements.filter(
      (p) => p.status === 'accepted' || p.status === 'placed' || p.status === 'board'
    ).length;
    const completed = baseFilteredPlacements.filter((p) => p.status === 'completed').length;
    return { total, active, hired, board, completed };
  }, [baseFilteredPlacements]);

  // Filtered & Sorted placements
  const organizedPlacements = useMemo(() => {
    let list = [...baseFilteredPlacements];

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((p) => {
        const student = (users || []).find((u) => u.uid === p.studentId || u.email === p.studentEmail);
        const schoolName = schools.find((s) => s.id === student?.school)?.name || student?.school || '';
        return (
          (p.studentName || '').toLowerCase().includes(q) ||
          (p.opportunityTitle || '').toLowerCase().includes(q) ||
          (p.organizationName || '').toLowerCase().includes(q) ||
          (p.mentorName || '').toLowerCase().includes(q) ||
          (p.studentEmail || '').toLowerCase().includes(q) ||
          (student?.email || '').toLowerCase().includes(q) ||
          schoolName.toLowerCase().includes(q)
        );
      });
    }

    // Opportunity dropdown filter
    if (filterOpportunityId !== 'all') {
      list = list.filter((p) => (p.opportunityId || 'unassigned') === filterOpportunityId);
    }

    // Status / Stage filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'active') {
        list = list.filter((p) => p.status === 'active');
      } else if (statusFilter === 'hired') {
        list = list.filter((p) => p.status === 'hired' || !!p.isHiredByOrg);
      } else if (statusFilter === 'board') {
        list = list.filter((p) => p.status === 'accepted' || p.status === 'placed' || p.status === 'board');
      } else if (statusFilter === 'completed') {
        list = list.filter((p) => p.status === 'completed');
      }
    }

    // Sort order
    if (sortBy === 'name_asc') {
      return sortStudentsByLastName(
        list as any[],
        (p: any) => p.studentName,
        (p: any) => (users || []).find((u: any) => u.uid === p.studentId || u.email === p.studentEmail)
      );
    } else if (sortBy === 'name_desc') {
      const sortedAsc = sortStudentsByLastName(
        list as any[],
        (p: any) => p.studentName,
        (p: any) => (users || []).find((u: any) => u.uid === p.studentId || u.email === p.studentEmail)
      );
      return sortedAsc.reverse();
    } else if (sortBy === 'opp_asc') {
      return [...list].sort((a, b) => (a.opportunityTitle || '').localeCompare(b.opportunityTitle || ''));
    } else if (sortBy === 'opp_desc') {
      return [...list].sort((a, b) => (b.opportunityTitle || '').localeCompare(a.opportunityTitle || ''));
    } else if (sortBy === 'program_asc') {
      return [...list].sort((a, b) => {
        const oppA = opportunities.find((o) => o.id === a.opportunityId);
        const oppB = opportunities.find((o) => o.id === b.opportunityId);
        const progA = (oppA?.careerCluster || oppA?.industry || '').toLowerCase();
        const progB = (oppB?.careerCluster || oppB?.industry || '').toLowerCase();
        return progA.localeCompare(progB);
      });
    } else if (sortBy === 'program_desc') {
      return [...list].sort((a, b) => {
        const oppA = opportunities.find((o) => o.id === a.opportunityId);
        const oppB = opportunities.find((o) => o.id === b.opportunityId);
        const progA = (oppA?.careerCluster || oppA?.industry || '').toLowerCase();
        const progB = (oppB?.careerCluster || oppB?.industry || '').toLowerCase();
        return progB.localeCompare(progA);
      });
    } else if (sortBy === 'school') {
      return [...list].sort((a, b) => {
        const studentA = (users || []).find((u) => u.uid === a.studentId || u.email === a.studentEmail);
        const studentB = (users || []).find((u) => u.uid === b.studentId || u.email === b.studentEmail);
        const schoolA = (schools.find((s) => s.id === studentA?.school)?.name || studentA?.school || '').toLowerCase();
        const schoolB = (schools.find((s) => s.id === studentB?.school)?.name || studentB?.school || '').toLowerCase();
        return schoolA.localeCompare(schoolB);
      });
    } else if (sortBy === 'status') {
      const statusOrder: Record<string, number> = {
        hired: 1,
        active: 2,
        placed: 3,
        accepted: 4,
        completed: 5,
        terminated: 6,
      };
      return [...list].sort((a, b) => (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99));
    } else if (sortBy === 'status_desc') {
      const statusOrder: Record<string, number> = {
        hired: 1,
        active: 2,
        placed: 3,
        accepted: 4,
        completed: 5,
        terminated: 6,
      };
      return [...list].sort((a, b) => (statusOrder[b.status] || 99) - (statusOrder[a.status] || 99));
    }

    return list;
  }, [baseFilteredPlacements, searchQuery, filterOpportunityId, statusFilter, sortBy, users, schools, opportunities]);

  // Group placements by opportunity for organization, superadmin, regional_admin, and advisor views
  const organizedGroupedPlacements = useMemo<Record<string, typeof organizedPlacements> | null>(() => {
    if (!['organization', 'superadmin', 'regional_admin', 'advisor'].includes(userRole)) return null;
    const groups: Record<string, typeof organizedPlacements> = {};
    organizedPlacements.forEach((p) => {
      const key = p.opportunityId || 'unassigned';
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });
    return groups;
  }, [organizedPlacements, userRole]);

  // ==========================================
  // PROGRESS BAR LOGIC & RENDERER (PRESERVED)
  // ==========================================
  const getStepState = (step: string, placement: any) => {
    if (step === 'BOARD') {
      const isComp = ['accepted', 'placed', 'hired', 'active', 'completed'].includes(placement.status);
      return {
        isCompleted: isComp,
        isActive: !isComp,
      };
    }
    if (step === 'EVAL 1') {
      const isComp =
        placement.status === 'hired' ||
        placement.eval1?.status === 'released';
      const isActive = !isComp && ['accepted', 'placed', 'active'].includes(placement.status);
      return { isCompleted: isComp, isActive };
    }
    if (step === 'EVAL 2') {
      const isComp =
        placement.status === 'hired' ||
        placement.eval2?.status === 'released';
      const isActive =
        placement.eval1?.status === 'released' &&
        !isComp;
      return { isCompleted: isComp, isActive };
    }
    if (step === 'HIRED') {
      const isComp =
        placement.status === 'hired' || !!placement.isHiredByOrg;
      const isActive = !isComp;
      return { isCompleted: isComp, isActive };
    }
    return {
      isCompleted: false,
      isActive: false,
    };
  };

  const renderProgressBar = (placement: any, isCompact = false) => {
    const steps = ['BOARD', 'EVAL 1', 'EVAL 2', 'HIRED'];

    return (
      <div className={`flex items-center justify-between gap-1 md:gap-2 py-1 ${isCompact ? 'w-full max-w-[280px]' : 'w-full max-w-[340px]'}`}>
        {steps.map((step) => {
          const { isCompleted, isActive } = getStepState(step, placement);

          if (step === 'BOARD') {
            return (
              <div 
                key={step} 
                className="flex flex-col items-center shrink-0 cursor-default"
                title={`Onboarding: ${isCompleted ? 'Completed & Placed' : 'Pending Onboarding'}`}
              >
                <div
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center border transition-all relative ${
                    isCompleted
                      ? 'bg-white border-2 border-[#0094c8] text-[#0094c8] shadow-xs ring-2 ring-[#0094c8]/10'
                      : 'bg-slate-50 border border-slate-200 text-slate-400 shadow-2xs'
                  }`}
                >
                  <ClipboardCheck className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isCompleted ? 'text-[#0094c8]' : 'text-slate-400'}`} />
                  {isCompleted && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#0094c8] rounded-full border border-white shadow-2xs" />
                  )}
                </div>
                <span
                  className={`text-[7px] sm:text-[8px] font-black mt-1 uppercase tracking-tight text-center block font-mono ${
                    isCompleted ? 'text-[#0094c8]' : 'text-slate-400'
                  }`}
                >
                  BOARD
                </span>
              </div>
            );
          }

          if (step === 'HIRED') {
            return (
              <div 
                key={step} 
                className="flex flex-col items-center shrink-0 cursor-default"
                title={`Permanent Placement: ${isCompleted ? 'Hired by Organization' : 'Pending Completion / Decision'}`}
              >
                <div
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center border transition-all ${
                    isCompleted
                      ? 'bg-emerald-600 border border-emerald-500 text-white shadow-xs'
                      : 'bg-slate-100 border border-slate-200 text-slate-400 shadow-2xs'
                  }`}
                >
                  <div
                    className={`w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full border flex items-center justify-center ${
                      isCompleted ? 'border-white text-white' : 'border-slate-350 text-slate-400'
                    }`}
                  >
                    <Check className="w-2.5 h-2.5 stroke-[3]" />
                  </div>
                </div>
                <span
                  className={`text-[7px] sm:text-[8px] font-black mt-1 uppercase tracking-tight text-center block font-mono ${
                    isCompleted ? 'text-emerald-700 font-extrabold' : 'text-slate-400'
                  }`}
                >
                  HIRED
                </span>
              </div>
            );
          }

          // EVAL 1 and EVAL 2
          return (
            <div 
              key={step} 
              className="flex-1 flex flex-col items-center min-w-[28px] sm:min-w-[40px] px-0.5 cursor-default"
              title={`${step}: ${
                isCompleted
                  ? 'Completed & Released'
                  : isActive
                  ? 'In Progress / Open for Review'
                  : 'Pending Milestone'
              }`}
            >
              <div
                className={`w-full h-1.5 rounded-full transition-all duration-300 ${
                  isCompleted
                    ? 'bg-[#0094c8] shadow-2xs shadow-[#0094c8]/20'
                    : isActive
                    ? 'bg-blue-300 animate-pulse'
                    : 'bg-slate-200/80 border border-slate-200/40'
                }`}
              />
              <span
                className={`text-[7px] sm:text-[8px] font-black mt-1.5 uppercase tracking-tight text-center block font-mono ${
                  isCompleted
                    ? 'text-[#0094c8]'
                    : isActive
                    ? 'text-blue-600 font-bold'
                    : 'text-slate-400'
                }`}
              >
                {step}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={isDetailView ? '' : 'space-y-6'}>
      <HeroBanner 
        title="PLACEMENTS"
        subtitle="MANAGE PROFESSIONAL WORK-BASED LEARNING ASSIGNMENTS"
        pageKey="placements"
        height={isDetailView ? '0px' : '187.5px'}
        className={isDetailView ? 'opacity-0 mb-0' : 'opacity-100 mb-6'}
      />

      <AnimatePresence mode="wait">
        {!isDetailView ? (
          <motion.div
            key="list"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="w-[95%] mx-auto mt-6 space-y-4"
          >
            {/* Filter & Toolbar Controls */}
            <div className="bg-white rounded-xl border border-slate-200/80 p-3.5 shadow-2xs">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="flex-1 w-full">
                  <SearchBar 
                    value={searchQuery} 
                    onChange={setSearchQuery} 
                    placeholder="Search placements (student, opportunity, supervisor)..." 
                  />
                </div>

                {/* Reset Filters */}
                {(statusFilter !== 'all' || filterOpportunityId !== 'all' || searchQuery) && (
                  <button
                    type="button"
                    onClick={() => {
                      setStatusFilter('all');
                      setFilterOpportunityId('all');
                      setSearchQuery('');
                    }}
                    className="px-2.5 py-1.5 text-[10px] font-bold text-slate-500 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 flex items-center gap-1 transition-all uppercase tracking-wider cursor-pointer self-start sm:self-center"
                    title="Reset all filters"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Reset Filters</span>
                  </button>
                )}
              </div>
            </div>

            {/* Empty States */}
            {baseFilteredPlacements.length === 0 && !searchQuery ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-sm max-w-xl mx-auto space-y-4">
                <div className="mx-auto w-16 h-16 bg-slate-50 text-slate-400 rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">No Placements Registered</h3>
                <p className="text-sm text-slate-500">
                  Once an application is approved and training agreements are completed, the placement will appear here for progress tracking and evaluations.
                </p>
                <button 
                  onClick={() => setCurrentView('applications')}
                  className="px-6 py-2.5 bg-[#0094c8] text-white text-[10px] font-black rounded-lg shadow-sm uppercase tracking-widest cursor-pointer"
                >
                  Check Application Status
                </button>
              </div>
            ) : organizedPlacements.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center shadow-2xs space-y-3 max-w-lg mx-auto">
                <div className="mx-auto w-10 h-10 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center">
                  <Filter className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800">No matching placements</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    No placements match your current search and stage filter criteria.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setStatusFilter('all');
                    setFilterOpportunityId('all');
                    setSearchQuery('');
                  }}
                  className="px-4 py-1.5 bg-[#0094c8] text-white text-xs font-bold rounded-lg uppercase tracking-wider hover:bg-[#007ba6] cursor-pointer inline-flex items-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>View All Placements</span>
                </button>
              </div>
            ) : ['organization', 'superadmin', 'regional_admin', 'advisor'].includes(userRole) && organizedGroupedPlacements ? (
              /* Grouped View for Organizations & Admins */
              viewMode === 'table' ? (
                /* High-Density Table View */
                <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono">
                          <th className="py-3 px-4">
                            <button
                              type="button"
                              onClick={() => setSortBy(sortBy === 'name_asc' ? 'name_desc' : 'name_asc')}
                              className="group inline-flex items-center gap-1.5 hover:text-slate-700 transition-colors uppercase cursor-pointer"
                              title="Sort by Candidate Name"
                            >
                              <span>Placement Candidate</span>
                              <ArrowUpDown className={`w-3 h-3 ${sortBy === 'name_asc' || sortBy === 'name_desc' ? 'text-[#0094c8]' : 'text-slate-300 group-hover:text-slate-500'}`} />
                            </button>
                          </th>
                          <th className="py-3 px-4">
                            <button
                              type="button"
                              onClick={() => setSortBy(sortBy === 'opp_asc' ? 'opp_desc' : 'opp_asc')}
                              className="group inline-flex items-center gap-1.5 hover:text-slate-700 transition-colors uppercase cursor-pointer"
                              title="Sort by Opportunity"
                            >
                              <span>Opportunity</span>
                              <ArrowUpDown className={`w-3 h-3 ${sortBy === 'opp_asc' || sortBy === 'opp_desc' ? 'text-[#0094c8]' : 'text-slate-300 group-hover:text-slate-500'}`} />
                            </button>
                          </th>
                          <th className="py-3 px-4">
                            <button
                              type="button"
                              onClick={() => setSortBy(sortBy === 'school' ? 'name_asc' : 'school')}
                              className="group inline-flex items-center gap-1.5 hover:text-slate-700 transition-colors uppercase cursor-pointer"
                              title="Sort by School"
                            >
                              <span>School & Mentor</span>
                              <ArrowUpDown className={`w-3 h-3 ${sortBy === 'school' ? 'text-[#0094c8]' : 'text-slate-300 group-hover:text-slate-500'}`} />
                            </button>
                          </th>
                          <th className="py-3 px-4 text-center">Progress Timeline</th>
                          <th className="py-3 px-4 relative">
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => setSortBy(sortBy === 'status' ? 'status_desc' : 'status')}
                                className="group inline-flex items-center gap-1 hover:text-slate-700 transition-colors uppercase cursor-pointer"
                                title="Sort by Status"
                              >
                                <span>Status</span>
                                <ArrowUpDown className={`w-3 h-3 ${sortBy === 'status' || sortBy === 'status_desc' ? 'text-[#0094c8]' : 'text-slate-300 group-hover:text-slate-500'}`} />
                              </button>
                              <div className="relative" ref={statusDropdownRef}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setStatusDropdownOpen(!statusDropdownOpen);
                                  }}
                                  className={`p-1 rounded transition-all cursor-pointer flex items-center justify-center ${
                                    statusFilter !== 'all'
                                      ? 'bg-[#0094c8] text-white shadow-2xs'
                                      : 'bg-slate-100 text-slate-500 hover:text-slate-800 hover:bg-slate-200'
                                  }`}
                                  title="Filter by Status"
                                >
                                  <Filter className="w-3.5 h-3.5" />
                                  {statusFilter !== 'all' && (
                                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400 border border-white" />
                                  )}
                                </button>

                                {statusDropdownOpen && (
                                  <div
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute left-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50 text-xs font-normal normal-case text-slate-800"
                                  >
                                    <div className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest font-mono border-b border-slate-100 flex items-center justify-between">
                                      <span>Filter Status</span>
                                      {statusFilter !== 'all' && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setStatusFilter('all');
                                            setStatusDropdownOpen(false);
                                          }}
                                          className="text-[#0094c8] hover:underline cursor-pointer lowercase font-sans text-[10px]"
                                        >
                                          clear
                                        </button>
                                      )}
                                    </div>
                                    {[
                                      { key: 'all', label: 'All Placements', count: placementCounts.total },
                                      { key: 'active', label: 'Active', count: placementCounts.active },
                                      { key: 'hired', label: 'Hired', count: placementCounts.hired },
                                      { key: 'board', label: 'Boarding', count: placementCounts.board },
                                      { key: 'completed', label: 'Completed', count: placementCounts.completed },
                                    ].map((opt) => (
                                      <button
                                        key={opt.key}
                                        type="button"
                                        onClick={() => {
                                          setStatusFilter(opt.key as any);
                                          setStatusDropdownOpen(false);
                                        }}
                                        className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between font-medium cursor-pointer transition-colors ${
                                          statusFilter === opt.key ? 'text-[#0094c8] font-bold bg-blue-50/50' : 'text-slate-700'
                                        }`}
                                      >
                                        <div className="flex items-center gap-2">
                                          <span>{opt.label}</span>
                                          <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                                            statusFilter === opt.key ? 'bg-[#0094c8]/10 text-[#0094c8]' : 'bg-slate-100 text-slate-500'
                                          }`}>
                                            {opt.count}
                                          </span>
                                        </div>
                                        {statusFilter === opt.key && <Check className="w-3.5 h-3.5 text-[#0094c8]" />}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </th>
                          <th className="py-3 px-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-xs">
                        {organizedPlacements.map((placement) => {
                          const studentInfo = (users || []).find((u) => u.uid === placement.studentId || u.email === placement.studentEmail);
                          const opp = opportunities.find((o) => o.id === placement.opportunityId);
                          const schoolName = schools.find((s) => s.id === studentInfo?.school)?.name || studentInfo?.school || 'School Not Listed';

                          return (
                            <tr 
                              key={placement.id} 
                              className="hover:bg-slate-50/80 transition-colors cursor-pointer"
                              onClick={() => {
                                sessionStorage.setItem('selectedPlacementId', placement.id);
                                setCurrentView('placement-detail');
                              }}
                            >
                              <td className="py-3 px-4">
                                <div className="flex items-center space-x-3">
                                  <div className="w-11 h-11 rounded-xl overflow-hidden shrink-0 border border-slate-200/90 bg-slate-50 shadow-xs flex items-center justify-center">
                                    <StudentAvatar
                                      studentId={placement.studentId}
                                      studentName={placement.studentName}
                                      studentEmail={placement.studentEmail}
                                      profile={studentInfo}
                                    />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1">
                                      <span className="font-bold text-slate-900 truncate uppercase">
                                        {formatLastNameFirst(placement.studentName, studentInfo)}
                                      </span>
                                      {studentInfo?.verified && (
                                        <span className="inline-flex items-center gap-0.5 text-[7px] font-black text-sky-700 bg-sky-50 px-1 py-0.2 rounded border border-sky-100 uppercase" title="Verified Account">
                                          <Check className="w-2 h-2 text-sky-600" />
                                        </span>
                                      )}
                                    </div>
                                    <span className="text-[10px] text-slate-400 font-mono block truncate">
                                      {placement.studentEmail || studentInfo?.email || 'No email recorded'}
                                    </span>
                                  </div>
                                </div>
                              </td>

                              <td className="py-3 px-4 font-semibold text-slate-800">
                                <span className="truncate max-w-[200px] block" title={placement.opportunityTitle}>
                                  {placement.opportunityTitle}
                                </span>
                                <span className="text-[10px] text-slate-400 font-mono block truncate uppercase">
                                  {placement.organizationName}
                                </span>
                              </td>

                              <td className="py-3 px-4">
                                <div className="min-w-0">
                                  <span className="font-medium text-slate-700 block truncate">
                                    {schoolName}
                                  </span>
                                  {placement.mentorName && (
                                    <span className="text-[10px] text-slate-400 block truncate uppercase">
                                      Mentor: {placement.mentorName.replace(/\(.*?\)/g, '').trim()}
                                    </span>
                                  )}
                                </div>
                              </td>

                              <td className="py-3 px-4">
                                <div className="flex items-center justify-center">
                                  {renderProgressBar(placement, true)}
                                </div>
                              </td>

                              <td className="py-3 px-4 whitespace-nowrap">
                                <span className={`px-2.5 py-1 rounded-lg border text-xs font-bold tracking-wider flex items-center gap-1.5 font-mono ${
                                  placement.status === 'hired'
                                    ? 'border-emerald-300 bg-emerald-50/70 text-emerald-700'
                                    : placement.status === 'active'
                                    ? 'border-blue-300 bg-blue-50/70 text-blue-700'
                                    : placement.status === 'completed'
                                    ? 'border-teal-300 bg-teal-50/70 text-teal-700'
                                    : 'border-amber-300 bg-amber-50/70 text-amber-700'
                                }`}>
                                  {placement.status === 'hired' || placement.status === 'completed' ? (
                                    <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                                  ) : (
                                    <Clock className="w-3.5 h-3.5 text-blue-600" />
                                  )}
                                  {placement.status.toUpperCase()}
                                </span>
                              </td>

                              <td className="py-3 px-4 text-right whitespace-nowrap">
                                <div className="inline-flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                  <a
                                    href={studentInfo?.resumeUrl || '#'}
                                    target={studentInfo?.resumeUrl ? '_blank' : undefined}
                                    rel="noreferrer"
                                    onClick={(e) => {
                                      if (!studentInfo?.resumeUrl) e.preventDefault();
                                    }}
                                    className={`w-9 h-9 rounded-lg border border-sky-200 bg-sky-50 text-sky-600 hover:bg-sky-100 flex items-center justify-center transition-colors shadow-2xs ${
                                      !studentInfo?.resumeUrl ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                    }`}
                                    title={studentInfo?.resumeUrl ? 'View / Download Candidate Resume' : 'No resume uploaded'}
                                  >
                                    <FileText className="w-4 h-4" />
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      sessionStorage.setItem('selectedPlacementId', placement.id);
                                      setCurrentView('placement-detail');
                                    }}
                                    className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 flex items-center justify-center transition-colors cursor-pointer shadow-2xs"
                                    title="View & Manage Placement Details"
                                  >
                                    <ChevronRight className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                /* Opportunity Grouped Card View (matches image design) */
                <div className="space-y-4">
                  {Object.entries(organizedGroupedPlacements as Record<string, any[]>).map(([oppId, placementsInOpp]) => {
                    const opportunity = opportunities.find((o) => o.id === oppId);
                    const isCollapsed = !!collapsedOpps[oppId];
                    const activeCount = placementsInOpp.filter((p) => p.status === 'active').length;
                    const hiredCount = placementsInOpp.filter((p) => p.status === 'hired' || !!p.isHiredByOrg).length;
                    const orgProfile = (users || []).find(
                      (u) => u.orgName === (opportunity?.organizationName || placementsInOpp[0]?.organizationName)
                    );

                    return (
                      <div key={oppId} className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
                        {/* Compact Opportunity Header matching image */}
                        <div
                          onClick={() =>
                            setCollapsedOpps((prev) => ({
                              ...prev,
                              [oppId]: !prev[oppId],
                            }))
                          }
                          className="flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:px-6 sm:py-4 cursor-pointer select-none bg-white hover:bg-slate-50/50 border-b border-slate-200/80 transition-colors gap-3"
                        >
                          <div className="flex items-center space-x-4 min-w-0">
                            <div className="w-16 h-16 rounded-xl border border-slate-200 bg-white p-1 flex items-center justify-center shrink-0 shadow-2xs">
                              {(() => {
                                const logoUrl = orgProfile?.avatar || opportunity?.externalLogoUrl;
                                const hasLogo = logoUrl && isUrl(logoUrl);
                                if (!hasLogo) {
                                  return (
                                    <ProfessionalOrgFallback
                                      orgName={
                                        opportunity?.organizationName ||
                                        placementsInOpp[0]?.organizationName ||
                                        'Company'
                                      }
                                      orgId={opportunity?.organizationId || undefined}
                                      variant="square"
                                      className="w-full h-full text-[9px]"
                                      sector={opportunity?.careerCluster || opportunity?.industry}
                                    />
                                  );
                                }
                                return (
                                  <StudentAvatar
                                    studentId={opportunity?.organizationId || undefined}
                                    studentName={
                                      opportunity?.organizationName ||
                                      placementsInOpp[0]?.organizationName ||
                                      'Company'
                                    }
                                    profile={orgProfile}
                                    imageUrl={logoUrl}
                                    className="w-full h-full object-cover"
                                  />
                                );
                              })()}
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-sm sm:text-base font-extrabold text-[#0f2d4a] uppercase tracking-tight truncate">
                                {opportunity?.title ||
                                  placementsInOpp[0]?.opportunityTitle ||
                                  'Unknown Opportunity'}
                              </h3>
                              <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500 mt-0.5">
                                {(opportunity?.careerCluster || opportunity?.industry) && (
                                  <span className="font-semibold text-slate-600 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-200 text-[11px]">
                                    {opportunity.careerCluster || opportunity.industry}
                                  </span>
                                )}
                                {(opportunity?.term || opportunity?.semester || placementsInOpp[0]?.term) && (
                                  <span className="text-slate-400 font-medium">
                                    • {opportunity?.term || opportunity?.semester || placementsInOpp[0]?.term}
                                  </span>
                                )}
                                {(opportunity?.externalContactEmail || placementsInOpp[0]?.externalContactEmail) && (
                                  <span className="text-[#0094c8] lowercase flex items-center gap-0.5">
                                    <Mail className="w-2.5 h-2.5" />
                                    {opportunity?.externalContactEmail || placementsInOpp[0]?.externalContactEmail}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2.5 shrink-0 self-end sm:self-center">
                            {/* Placement count pills */}
                            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase font-bold">
                              <span className="px-2.5 py-1 rounded-md bg-white text-slate-700 border border-slate-200">
                                {placementsInOpp.length} Total
                              </span>
                              {activeCount > 0 && (
                                <span className="px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
                                  {activeCount} Active
                                </span>
                              )}
                              {hiredCount > 0 && (
                                <span className="px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  {hiredCount} Hired
                                </span>
                              )}
                            </div>

                            <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50 flex items-center justify-center transition-colors">
                              {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                            </div>
                          </div>
                        </div>

                        {/* Placements List under Opportunity */}
                        {!isCollapsed && (
                          <div>
                            {/* Table-like aligned header bar */}
                            <div className="hidden xl:flex items-center px-6 py-2.5 bg-slate-50/70 border-b border-slate-200/70 text-[10px] font-black uppercase tracking-widest text-slate-400 font-mono select-none">
                              <div className="w-64 shrink-0">Placement Candidate</div>
                              <div className="w-60 shrink-0">School & Mentor</div>
                              <div className="flex-1 min-w-[320px] max-w-2xl px-2 text-center">Progress Timeline</div>
                              <div className="w-32 shrink-0 flex items-center justify-center gap-1.5 relative">
                                <span>Status</span>
                                <div className="relative" ref={cardStatusDropdownRef}>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCardStatusDropdownOpen(!cardStatusDropdownOpen);
                                    }}
                                    className={`p-1 rounded transition-all cursor-pointer flex items-center justify-center ${
                                      statusFilter !== 'all'
                                        ? 'bg-[#0094c8] text-white shadow-2xs'
                                        : 'bg-slate-100 text-slate-500 hover:text-slate-800 hover:bg-slate-200'
                                    }`}
                                    title="Filter by Status"
                                  >
                                    <Filter className="w-3 h-3" />
                                    {statusFilter !== 'all' && (
                                      <span className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full bg-amber-400 border border-white" />
                                    )}
                                  </button>
                                  {cardStatusDropdownOpen && (
                                    <div
                                      onClick={(e) => e.stopPropagation()}
                                      className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50 text-xs font-normal normal-case text-slate-800 text-left"
                                    >
                                      <div className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest font-mono border-b border-slate-100 flex items-center justify-between">
                                        <span>Filter Status</span>
                                        {statusFilter !== 'all' && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setStatusFilter('all');
                                              setCardStatusDropdownOpen(false);
                                            }}
                                            className="text-[#0094c8] hover:underline cursor-pointer lowercase font-sans text-[10px]"
                                          >
                                            clear
                                          </button>
                                        )}
                                      </div>
                                      {[
                                        { key: 'all', label: 'All Placements', count: placementCounts.total },
                                        { key: 'active', label: 'Active', count: placementCounts.active },
                                        { key: 'hired', label: 'Hired', count: placementCounts.hired },
                                        { key: 'board', label: 'Boarding', count: placementCounts.board },
                                        { key: 'completed', label: 'Completed', count: placementCounts.completed },
                                      ].map((opt) => (
                                        <button
                                          key={opt.key}
                                          type="button"
                                          onClick={() => {
                                            setStatusFilter(opt.key as any);
                                            setCardStatusDropdownOpen(false);
                                          }}
                                          className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between font-medium cursor-pointer transition-colors ${
                                            statusFilter === opt.key ? 'text-[#0094c8] font-bold bg-blue-50/50' : 'text-slate-700'
                                          }`}
                                        >
                                          <div className="flex items-center gap-2">
                                            <span>{opt.label}</span>
                                            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                                              statusFilter === opt.key ? 'bg-[#0094c8]/10 text-[#0094c8]' : 'bg-slate-100 text-slate-500'
                                            }`}>
                                              {opt.count}
                                            </span>
                                          </div>
                                          {statusFilter === opt.key && <Check className="w-3.5 h-3.5 text-[#0094c8]" />}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="w-44 shrink-0 text-right pr-2">Actions</div>
                            </div>

                            <div className="divide-y divide-slate-100">
                              {placementsInOpp.map((placement) => {
                                const studentInfo = (users || []).find((u) => u.uid === placement.studentId || u.email === placement.studentEmail);
                                const schoolName = schools.find((s) => s.id === studentInfo?.school)?.name || studentInfo?.school || 'School Not Listed';

                                const statusBorder =
                                  placement.status === 'hired'
                                    ? 'border-l-emerald-500'
                                    : placement.status === 'active'
                                    ? 'border-l-blue-500'
                                    : placement.status === 'completed'
                                    ? 'border-l-teal-500'
                                    : 'border-l-amber-400';

                                return (
                                  <div
                                    key={placement.id}
                                    className={`border-l-4 ${statusBorder} bg-white hover:bg-slate-50/50 transition-colors cursor-pointer`}
                                    onClick={() => {
                                      sessionStorage.setItem('selectedPlacementId', placement.id);
                                      setCurrentView('placement-detail');
                                    }}
                                  >
                                    <div className="flex flex-col xl:flex-row xl:items-center justify-between p-3.5 sm:px-6 sm:py-3.5 gap-3 xl:gap-4">
                                      {/* Left: Avatar + Candidate Name + Email (w-64) */}
                                      <div className="flex items-center space-x-3.5 min-w-0 xl:w-64 shrink-0">
                                        <div className="w-11 h-11 rounded-xl overflow-hidden shrink-0 border border-slate-200/90 bg-slate-50 shadow-xs flex items-center justify-center">
                                          <StudentAvatar
                                            studentId={placement.studentId}
                                            studentName={placement.studentName}
                                            studentEmail={placement.studentEmail}
                                            profile={studentInfo}
                                          />
                                        </div>
                                        <div className="min-w-0">
                                          <div className="flex items-center gap-1.5 flex-wrap">
                                            <h4 className="text-sm font-bold text-slate-900 uppercase tracking-tight truncate">
                                              {formatLastNameFirst(placement.studentName, studentInfo)}
                                            </h4>
                                            {studentInfo?.verified && (
                                              <span className="inline-flex items-center gap-1 text-[8px] font-black text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded border border-sky-200 uppercase tracking-wider font-mono shrink-0" title="Verified Account">
                                                <Check className="w-2.5 h-2.5 text-sky-600" />
                                                Verified
                                              </span>
                                            )}
                                          </div>
                                          <p className="text-xs text-slate-400 truncate font-mono mt-0.5">
                                            {placement.studentEmail || studentInfo?.email || 'No email recorded'}
                                          </p>
                                        </div>
                                      </div>

                                      {/* School & Mentor Column (w-60) */}
                                      <div className="flex flex-col xl:w-60 text-left shrink-0 min-w-0">
                                        <span className="text-xs sm:text-sm font-semibold text-slate-800 truncate" title={schoolName}>
                                          {schoolName}
                                        </span>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono truncate mt-0.5">
                                          {placement.mentorName ? `Mentor: ${placement.mentorName.replace(/\(.*?\)/g, '').trim()}` : 'Regional School District'}
                                        </span>
                                      </div>

                                      {/* Center: Step Progress Timeline (PRESERVED) */}
                                      <div className="flex-1 w-full min-w-[320px] max-w-2xl px-1 py-0.5 flex items-center justify-center">
                                        {renderProgressBar(placement)}
                                      </div>

                                      {/* Right: Status Badge & Action Buttons */}
                                      <div className="flex items-center justify-between xl:justify-end gap-3 shrink-0 pt-2 xl:pt-0 border-t xl:border-t-0 border-slate-100">
                                        {/* Outlined status pill matching screenshot (w-32) */}
                                        <div className="w-auto xl:w-32 shrink-0 flex items-center justify-start xl:justify-center">
                                          <span className={`px-2.5 py-1 rounded-lg border text-xs font-bold tracking-wider flex items-center gap-1.5 font-mono ${
                                            placement.status === 'hired'
                                              ? 'border-emerald-300 bg-emerald-50/70 text-emerald-700'
                                              : placement.status === 'active'
                                              ? 'border-blue-300 bg-blue-50/70 text-blue-700'
                                              : placement.status === 'completed'
                                              ? 'border-teal-300 bg-teal-50/70 text-teal-700'
                                              : 'border-amber-300 bg-amber-50/70 text-amber-700'
                                          }`}>
                                            {placement.status === 'hired' || placement.status === 'completed' ? (
                                              <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                                            ) : (
                                              <Clock className="w-3.5 h-3.5 text-blue-600" />
                                            )}
                                            {placement.status.toUpperCase()}
                                          </span>
                                        </div>

                                        {/* Action buttons (w-44) */}
                                        <div className="xl:w-44 shrink-0 flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                                          <a
                                            href={studentInfo?.resumeUrl || '#'}
                                            target={studentInfo?.resumeUrl ? '_blank' : undefined}
                                            rel="noreferrer"
                                            onClick={(e) => {
                                              if (!studentInfo?.resumeUrl) e.preventDefault();
                                            }}
                                            className={`w-9 h-9 rounded-lg border border-sky-200 bg-sky-50 text-sky-600 hover:bg-sky-100 hover:text-sky-700 flex items-center justify-center transition-colors shadow-2xs ${
                                              !studentInfo?.resumeUrl ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                            }`}
                                            title={studentInfo?.resumeUrl ? 'View / Download Candidate Resume' : 'No resume uploaded'}
                                          >
                                            <FileText className="w-4 h-4" />
                                          </a>

                                          <button
                                            type="button"
                                            onClick={() => {
                                              sessionStorage.setItem('selectedPlacementId', placement.id);
                                              setCurrentView('placement-detail');
                                            }}
                                            className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 flex items-center justify-center transition-colors cursor-pointer shadow-2xs"
                                            title="View & Manage Placement Details"
                                          >
                                            <ChevronRight className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            ) : isStudentView ? (
              /* Student View: Interactive Sortable & Filterable Table with Exact Same Visual Design */
              <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-slate-200/80 text-[10px] font-black uppercase tracking-widest text-slate-400 font-mono select-none">
                        <th className="py-3 px-6 text-left whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => setSortBy(sortBy === 'opp_asc' ? 'opp_desc' : 'opp_asc')}
                            className="group inline-flex items-center gap-1.5 hover:text-slate-700 transition-colors uppercase cursor-pointer"
                            title="Sort by Opportunity & Host"
                          >
                            <span>Opportunity & Host</span>
                            <ArrowUpDown className={`w-3 h-3 ${sortBy === 'opp_asc' || sortBy === 'opp_desc' ? 'text-[#0094c8]' : 'text-slate-300 group-hover:text-slate-500'}`} />
                          </button>
                        </th>
                        <th className="py-3 px-6 text-left whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => setSortBy(sortBy === 'program_asc' ? 'program_desc' : 'program_asc')}
                            className="group inline-flex items-center gap-1.5 hover:text-slate-700 transition-colors uppercase cursor-pointer"
                            title="Sort by Program & Mentor"
                          >
                            <span>Program & Mentor</span>
                            <ArrowUpDown className={`w-3 h-3 ${sortBy === 'program_asc' || sortBy === 'program_desc' ? 'text-[#0094c8]' : 'text-slate-300 group-hover:text-slate-500'}`} />
                          </button>
                        </th>
                        <th className="py-3 px-6 text-center whitespace-nowrap">
                          Progress Timeline
                        </th>
                        <th className="py-3 px-6 text-center relative whitespace-nowrap">
                          <div className="inline-flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setSortBy(sortBy === 'status' ? 'status_desc' : 'status')}
                              className="group inline-flex items-center gap-1 hover:text-slate-700 transition-colors uppercase cursor-pointer"
                              title="Sort by Status"
                            >
                              <span>Status</span>
                              <ArrowUpDown className={`w-3 h-3 ${sortBy === 'status' || sortBy === 'status_desc' ? 'text-[#0094c8]' : 'text-slate-300 group-hover:text-slate-500'}`} />
                            </button>

                            {/* Status Filter Icon & Choices Dropdown */}
                            <div className="relative" ref={studentStatusDropdownRef}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStudentStatusDropdownOpen(!studentStatusDropdownOpen);
                                }}
                                className={`p-1 rounded transition-all cursor-pointer flex items-center justify-center ${
                                  statusFilter !== 'all'
                                    ? 'bg-[#0094c8] text-white shadow-2xs'
                                    : 'bg-slate-100 text-slate-500 hover:text-slate-800 hover:bg-slate-200'
                                }`}
                                title="Filter by Status / Stage"
                              >
                                <Filter className="w-3.5 h-3.5" />
                                {statusFilter !== 'all' && (
                                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400 border border-white" />
                                )}
                              </button>

                              {studentStatusDropdownOpen && (
                                <div
                                  onClick={(e) => e.stopPropagation()}
                                  className="absolute left-1/2 -translate-x-1/2 mt-2 w-52 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50 text-xs font-normal normal-case text-slate-800 text-left"
                                >
                                  <div className="px-3 py-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest font-mono border-b border-slate-100 flex items-center justify-between">
                                    <span>Filter Status</span>
                                    {statusFilter !== 'all' && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setStatusFilter('all');
                                          setStudentStatusDropdownOpen(false);
                                        }}
                                        className="text-[#0094c8] hover:underline cursor-pointer lowercase font-sans text-[10px]"
                                      >
                                        clear
                                      </button>
                                    )}
                                  </div>
                                  {[
                                    { key: 'all', label: 'All Placements', count: placementCounts.total },
                                    { key: 'active', label: 'Active', count: placementCounts.active },
                                    { key: 'hired', label: 'Hired', count: placementCounts.hired },
                                    { key: 'board', label: 'Boarding', count: placementCounts.board },
                                    { key: 'completed', label: 'Completed', count: placementCounts.completed },
                                  ].map((opt) => (
                                    <button
                                      key={opt.key}
                                      type="button"
                                      onClick={() => {
                                        setStatusFilter(opt.key as any);
                                        setStudentStatusDropdownOpen(false);
                                      }}
                                      className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between font-medium cursor-pointer transition-colors ${
                                        statusFilter === opt.key ? 'text-[#0094c8] font-bold bg-blue-50/50' : 'text-slate-700'
                                      }`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span>{opt.label}</span>
                                        <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                                          statusFilter === opt.key ? 'bg-[#0094c8]/10 text-[#0094c8]' : 'bg-slate-100 text-slate-500'
                                        }`}>
                                          {opt.count}
                                        </span>
                                      </div>
                                      {statusFilter === opt.key && <Check className="w-3.5 h-3.5 text-[#0094c8]" />}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </th>
                        <th className="py-3 px-6 text-right whitespace-nowrap">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {organizedPlacements.map((placement) => {
                        const opp = opportunities.find((o) => o.id === placement.opportunityId);
                        const orgProfile = (users || []).find(
                          (u) => u.orgName === (opp?.organizationName || placement.organizationName)
                        );

                        const statusBorder =
                          placement.status === 'hired'
                            ? 'border-l-emerald-500'
                            : placement.status === 'active'
                            ? 'border-l-blue-500'
                            : placement.status === 'completed'
                            ? 'border-l-teal-500'
                            : 'border-l-amber-400';

                        return (
                          <tr
                            key={placement.id}
                            className={`group border-l-4 ${statusBorder} bg-white hover:bg-slate-50/50 transition-colors cursor-pointer`}
                            onClick={() => {
                              sessionStorage.setItem('selectedPlacementId', placement.id);
                              setCurrentView('placement-detail');
                            }}
                          >
                            {/* Column 1: Company Logo and Placement Title */}
                            <td className="py-3.5 px-6">
                              <div className="flex items-center space-x-3.5 min-w-[240px] max-w-xs">
                                <div className="w-11 h-11 rounded-xl border border-slate-200 bg-white p-1 flex items-center justify-center shrink-0 shadow-2xs">
                                  {(() => {
                                    const logoUrl = orgProfile?.avatar || opp?.externalLogoUrl;
                                    const hasLogo = logoUrl && isUrl(logoUrl);
                                    if (!hasLogo) {
                                      return (
                                        <ProfessionalOrgFallback
                                          orgName={placement.organizationName || 'Company'}
                                          orgId={placement.opportunityId}
                                          variant="square"
                                          className="w-full h-full text-[9px]"
                                          sector={opp?.careerCluster || opp?.industry}
                                        />
                                      );
                                    }
                                    return (
                                      <StudentAvatar
                                        studentId={placement.opportunityId}
                                        studentName={placement.organizationName}
                                        profile={orgProfile}
                                        imageUrl={logoUrl}
                                        className="w-full h-full object-cover"
                                      />
                                    );
                                  })()}
                                </div>
                                <div className="min-w-0">
                                  <h4 className="text-sm font-bold text-slate-900 uppercase tracking-tight truncate">
                                    {placement.opportunityTitle}
                                  </h4>
                                  <p className="text-xs text-slate-400 truncate font-mono mt-0.5 uppercase">
                                    {placement.organizationName}
                                  </p>
                                </div>
                              </div>
                            </td>

                            {/* Column 2: Program & Mentor */}
                            <td className="py-3.5 px-6">
                              <div className="flex flex-col text-left shrink-0 min-w-[200px] max-w-xs">
                                <span className="text-xs sm:text-sm font-semibold text-slate-800 truncate">
                                  {opp?.careerCluster || opp?.industry || 'Career Experience'}
                                </span>
                                <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-mono mt-0.5 truncate">
                                  {placement.startDate && (
                                    <span>Started: {placement.startDate}</span>
                                  )}
                                  {placement.mentorName && (
                                    <>
                                      <span>•</span>
                                      <span className="truncate">Mentor: {placement.mentorName.replace(/\(.*?\)/g, '').trim()}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Column 3: Step Progress Timeline */}
                            <td className="py-3.5 px-6">
                              <div className="flex items-center justify-center min-w-[300px]">
                                {renderProgressBar(placement)}
                              </div>
                            </td>

                            {/* Column 4: Outlined Status Pill */}
                            <td className="py-3.5 px-6 whitespace-nowrap text-center">
                              <div className="flex items-center justify-center">
                                <span className={`px-2.5 py-1 rounded-lg border text-xs font-bold tracking-wider inline-flex items-center gap-1.5 font-mono ${
                                  placement.status === 'hired'
                                    ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                                    : placement.status === 'active'
                                    ? 'border-blue-300 bg-blue-50 text-blue-700'
                                    : placement.status === 'completed'
                                    ? 'border-teal-300 bg-teal-50 text-teal-700'
                                    : 'border-amber-300 bg-amber-50 text-amber-700'
                                }`}>
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  {placement.status.toUpperCase()}
                                </span>
                              </div>
                            </td>

                            {/* Column 5: Action Toolbar */}
                            <td className="py-3.5 px-6 text-right whitespace-nowrap">
                              <div className="flex items-center justify-end">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    sessionStorage.setItem('selectedPlacementId', placement.id);
                                    setCurrentView('placement-detail');
                                  }}
                                  className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-800 flex items-center justify-center transition-colors cursor-pointer shadow-2xs"
                                  title="View Placement Details & Evaluations"
                                >
                                  <ChevronRight className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* Fallback Grid View */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {organizedPlacements.map((placement) => {
                  const studentInfo = (users || []).find((u) => u.uid === placement.studentId || u.email === placement.studentEmail);

                  return (
                    <div
                      key={placement.id}
                      className="bg-white rounded-xl border border-slate-200 shadow-2xs hover:shadow-md transition-all overflow-hidden cursor-pointer p-4 space-y-3"
                      onClick={() => {
                        sessionStorage.setItem('selectedPlacementId', placement.id);
                        setCurrentView('placement-detail');
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center space-x-2.5 min-w-0">
                          <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border border-slate-200 bg-slate-50">
                            <StudentAvatar
                              studentId={placement.studentId}
                              studentName={placement.studentName}
                              studentEmail={placement.studentEmail}
                              profile={studentInfo}
                            />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-sm font-bold text-slate-900 uppercase truncate">
                              {formatLastNameFirst(placement.studentName, studentInfo)}
                            </h4>
                            <p className="text-[10px] text-slate-400 uppercase font-mono truncate">
                              {placement.opportunityTitle}
                            </p>
                          </div>
                        </div>

                        <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase font-mono shrink-0 ${
                          placement.status === 'hired'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : placement.status === 'active'
                            ? 'bg-blue-50 text-blue-600 border border-blue-200'
                            : 'bg-slate-50 text-slate-500 border border-slate-200'
                        } border`}>
                          {placement.status}
                        </span>
                      </div>

                      {/* Progress bar in card */}
                      <div className="pt-2 border-t border-slate-100 flex justify-center">
                        {renderProgressBar(placement, true)}
                      </div>

                      <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                        <span className="truncate">{placement.organizationName}</span>
                        <button
                          type="button"
                          className="p-1.5 bg-blue-50 text-blue-600 hover:bg-[#0094c8] hover:text-white rounded-lg transition-all"
                          title="View Details"
                        >
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="details"
            initial={{ opacity: 0, y: 70 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <PlacementDetail />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
